import { ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import type { Town } from '@shared/types/town';
import { formatNumber, formatDate } from '@shared/utils/format';

/** 담당자 이름 → 아바타 색상 매핑 */
const staffColors: Record<string, { bg: string; text: string }> = {
    이현진: { bg: 'bg-blue-100', text: 'text-blue-600' },
    류지민: { bg: 'bg-purple-100', text: 'text-purple-600' },
    장지안: { bg: 'bg-green-100', text: 'text-green-600' },
    김호정: { bg: 'bg-amber-100', text: 'text-amber-600' },
    박유찬: { bg: 'bg-rose-100', text: 'text-rose-600' },
};

/** 구분 → 뱃지 색상 매핑 */
function getCategoryBadgeClass(category: string | null): string {
    if (!category) return 'bg-gray-100 text-gray-600';
    if (category.startsWith('KYS')) return 'bg-blue-50 text-blue-600';
    if (category === '유니콤') return 'bg-orange-50 text-orange-600';
    return 'bg-gray-100 text-gray-600';
}

interface TownTableProps {
    towns: Town[];
    isLoading: boolean;
    selectedTownId: number | null;
    onSelectTown: (townId: number) => void;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    onSort?: (column: string) => void;
}

function SortIcon({ column, sortBy, sortOrder }: { column: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) {
    if (sortBy !== column) return null;
    return sortOrder === 'asc'
        ? <ArrowUp className="w-3 h-3 inline ml-1" />
        : <ArrowDown className="w-3 h-3 inline ml-1" />;
}

export default function TownTable({ towns, isLoading, selectedTownId, onSelectTown, sortBy, sortOrder, onSort }: TownTableProps) {
    if (isLoading) {
        return (
            <div className="bg-white rounded shadow-sm border border-[#E6E9ED] overflow-hidden">
                <div className="p-8 text-center text-sm text-gray-400">불러오는 중...</div>
            </div>
        );
    }

    if (towns.length === 0) {
        return (
            <div className="bg-white rounded shadow-sm border border-[#E6E9ED] overflow-hidden">
                <div className="p-8 text-center text-sm text-gray-400">검색 결과가 없습니다</div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded shadow-sm border border-[#E6E9ED] overflow-hidden">
            <table className="w-full">
                <thead>
                    <tr className="bg-[#F5F7FA] border-b border-[#E6E9ED]">
                        <th className="text-left px-5 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => onSort?.('name')}>
                            단지명<SortIcon column="name" sortBy={sortBy} sortOrder={sortOrder} />
                        </th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider">주소</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => onSort?.('household_count')}>
                            세대수<SortIcon column="household_count" sortBy={sortBy} sortOrder={sortOrder} />
                        </th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => onSort?.('active_households')}>
                            사용 세대<SortIcon column="active_households" sortBy={sortBy} sortOrder={sortOrder} />
                        </th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => onSort?.('active_users_count')}>
                            사용자 수<SortIcon column="active_users_count" sortBy={sortBy} sortOrder={sortOrder} />
                        </th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider">구분</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider">발주처</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider">계약</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider">원패스</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider">비상벨</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider">공사 담당</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider">세팅 담당</th>
                        <th className="text-left px-4 py-4 text-xs font-semibold text-[#73879C] uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none" onClick={() => onSort?.('contracted_until')}>
                            계약 종료<SortIcon column="contracted_until" sortBy={sortBy} sortOrder={sortOrder} />
                        </th>
                        <th className="px-4 py-4"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#E6E9ED]">
                    {towns.map((town) => {
                        const staffColor = staffColors[town.setting_staff ?? ''] ?? {
                            bg: 'bg-gray-100',
                            text: 'text-gray-600',
                        };
                        const firstChar = town.setting_staff?.charAt(0) ?? '';

                        return (
                            <tr
                                key={town.id}
                                onClick={() => onSelectTown(town.id)}
                                className={`hover:bg-[#F5F7FA] cursor-pointer transition ${selectedTownId === town.id ? 'bg-[#ECF0F1]' : ''
                                    }`}
                            >
                                <td className="px-5 py-5">
                                    <div className="text-[14px] font-semibold text-[#2A3F54]">
                                        {town.name}
                                    </div>
                                    {town.vpn_number && (
                                        <div className="text-xs text-gray-400 mt-1">
                                            VPN {town.vpn_number}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-5 text-[13px] text-[#73879C]">
                                    {town.address ?? '-'}
                                </td>
                                <td className="px-4 py-5 text-[13px] text-[#73879C]">
                                    {formatNumber(town.household_count)}
                                </td>
                                <td className="px-4 py-5 text-[13px] text-[#73879C]">
                                    {formatNumber(town.active_households)}
                                </td>
                                <td className="px-4 py-5 text-[13px] text-[#73879C]">
                                    {formatNumber(town.active_users_count)}
                                </td>
                                <td className="px-4 py-5">
                                    {town.category ? (
                                        <span
                                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getCategoryBadgeClass(town.category)}`}
                                        >
                                            {town.category}
                                        </span>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-5 text-[13px] text-[#73879C]">
                                    {town.orderer ?? '-'}
                                </td>
                                <td className="px-4 py-5">
                                    <span className="text-sm text-gray-500">
                                        {town.contract_type ?? '-'}
                                    </span>
                                </td>
                                <td className="px-4 py-5">
                                    <span className={`text-sm ${town.sub_app ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                                        {town.sub_app || '-'}
                                    </span>
                                </td>
                                <td className="px-4 py-5">
                                    {(town as any).emergency_bell_types ? (
                                        <span className="text-sm text-red-600 font-medium">
                                            {(town as any).emergency_bell_types}
                                        </span>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-5">
                                    {town.construction_staff ? (
                                        <span className="text-sm text-[#2A3F54]">{town.construction_staff}</span>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-5">
                                    {town.setting_staff ? (
                                        <span className="text-sm text-[#2A3F54]">{town.setting_staff}</span>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-5 text-sm text-gray-500">
                                    {formatDate(town.contracted_until)}
                                </td>
                                <td className="px-4 py-5">
                                    <ChevronRight className="w-4 h-4 text-gray-300" />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
